import React, { useState, useEffect } from "react";
import { Select, SelectItem, Button, Input, Spinner, Card, CardBody } from "@heroui/react";
import { useFetchWithBody, usePost } from "../../api/reactQueryCustomHooks";
import { useSelector } from "react-redux";
import { apiRoute } from "../../constants/enum";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { saveAs } from "file-saver";
import { MainContainer } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTrash } from "@fortawesome/free-solid-svg-icons";

const ENUM_TEMP_TYPE = [{ key: "StockView", value: "Stock View" }];
const itemGridHeadClass = "bill-grid-style";

const ExcelMaster = () => {
  const { user } = useSelector(state => state.auth);

  const [templateList, setTemplateList] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selTemplateName, setSelTemplateName] = useState("ExlFormat");
  const [excelTemplateData, setExcelTemplateData] = useState([]);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateType, setNewTemplateType] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);

  const { data: templateData = [], isLoading: isTemplateLoading } = useFetchWithBody({
    url: apiRoute.mapping.getTemp,
    queryKey: "fetch_template_names",
    inputData: { db_user: user?.username },
  });

  const { data: excelData = [], isLoading: isExcelLoading, mutate: filterExcelTemplate } = usePost({
    url: apiRoute.mapping.GetExcelTemp,
    queryKey: "fetch_excel_template_data"
  });

  const { mutate: updateExcelTemplate } = usePost({ url: apiRoute.mapping.updateExcelTemplate });
  const { mutate: deleteExcelTemplate } = usePost({ url: apiRoute.mapping.deleteExcelTemplate });
  const { mutate: insertExcelTemplate } = usePost({
    url: apiRoute.mapping.insertExcelTemplate,
    queryKey: "inserttemplateexcel",
    onSuccessTasks: setExcelTemplateData
  });

  const { mutate: insertTemplate } = usePost({
    url: apiRoute.mapping.inserttemplete,
    queryKey: "Addtemplate",
    onSuccessTasks: () => {
      toast.success("Template Added Successfully");
      setNewTemplateName("");
    }
  });

  // Sync template list
  useEffect(() => { if (templateData?.length) setTemplateList(templateData); }, [templateData]);

  // Sync excel data
  useEffect(() => {
    if (!excelData?.length) return;
    setExcelTemplateData(excelData.map((row, index) => ({ ...row, id: row.detail_id || index + 1 })));
  }, [excelData]);

  const handleTemplateSelect = (keySet) => {
    const selectedId = Array.from(keySet)[0];
    const selected = templateList.find(t => t.TempleteID.toString() === selectedId);
    setSelectedTemplate(selected);
    setSelTemplateName(selected?.TempleteName || "ExlFormat");

    if (selected) filterExcelTemplate({ data: { tempid: selected.TempleteID } });
  };

  const handleAddNewTemplate = () => {
    setIsInputVisible(true);
    setSelectedTemplate(null);
    setExcelTemplateData([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newTemplateName || !newTemplateType) return toast.error("Fill all fields");
    insertTemplate({ data: { tname: newTemplateName, tmptype: newTemplateType } });
  };

  const handleProcessRowUpdate = async (newRow, oldRow) => {
    if (!selectedTemplate || !newRow.xl_col_no?.trim()) return oldRow;

    const detailId = parseInt(newRow.detail_id) || parseInt(newRow.id);
    if (!detailId || isNaN(detailId)) return oldRow;

    const payload = {
      detail_id: newRow.detail_id,
      templete_for: selectedTemplate.TempleteID,
      xl_col_no: newRow.xl_col_no,
      xl_col_name: newRow.xl_col_name,
      tb_col_name: newRow.tb_col_name,
    };

    const isExisting = excelTemplateData.some(r => r.detail_id === detailId);

    if (isExisting) {
      updateExcelTemplate({ data: payload });
    } else if (Object.values(payload).every(v => v !== null && v !== "")) {
      insertExcelTemplate({ data: { data: [payload] } });
    }

    return { ...newRow, id: detailId, detail_id: detailId };
  };

  const handleAddRow = () => {
    const newRow = {
      id: excelTemplateData.length ? Math.max(...excelTemplateData.map(r => r.id || 0)) + 1 : 1,
      detail_id: "",
      xl_col_no: "",
      xl_col_name: "",
      tb_col_name: "",
    };
    setExcelTemplateData(prev => [...prev, newRow]);
  };

  const handleDeleteRow = (row) => {
    if (!row?.detail_id || parseInt(row.detail_id) < 1) {
      setExcelTemplateData(prev => prev.filter(r => r !== row));
      return toast.info("Unsaved row removed");
    }

    if (!window.confirm("Are you sure you want to delete this row?")) return;

    deleteExcelTemplate(
      { data: { db_user: user.username, templete_for: selectedTemplate?.TempleteID, detail_id: row.detail_id } },
      {
        onSuccess: (response) => {
          const isSuccess = response.status === 200 || response.data?.status === 200 || (Array.isArray(response) && !response.length);
          if (isSuccess) {
            toast.success("Row deleted successfully");
            setExcelTemplateData(prev => prev.filter(r => parseInt(r.detail_id) !== parseInt(row.detail_id)));
          } else toast.error(response.data?.message || "Delete completed but unexpected response");
        },
        onError: (error) => toast.error(error.response?.data?.message || "Delete failed")
      }
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) setUploadedFile(file);
  };

  const handleDownloadExcel = () => {
    if (!selectedTemplate) return toast.error("Select Template", { position: "top-center" });

    const headers = excelData.map(ele => ele.xl_col_name);
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), selTemplateName + ".xlsx");
  };

  const columns = [
    { field: "xl_col_no", headerName: "Excel Column No", align: "center", headerAlign: "center", headerClassName: itemGridHeadClass, flex: 1, width: 150, editable: true },
    { field: "xl_col_name", headerName: "Excel Column Name", align: "center", headerAlign: "center", headerClassName: itemGridHeadClass, flex: 1, width: 200, editable: true },
    { field: "tb_col_name", headerName: "Table Column Name", align: "center", headerAlign: "center", headerClassName: itemGridHeadClass, flex: 1, width: 200, editable: true },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      width: 100,
      flex: 1,
      sortable: false,
      headerClassName: itemGridHeadClass,
      renderCell: (params) => (
        <Button color="danger" isIconOnly size="sm" variant="light" onPress={() => handleDeleteRow(params.row)} isDisabled={!selectedTemplate}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      )
    }
  ];

  return (
    <MainContainer pageName="Excel Template Master" breadcrumbs={["Settings", "Excel Template Master"]}>
      <Card shadow="sm" className="mb-3 w-full p-3 bg-white">
        <CardBody>
          {isTemplateLoading ? <p>Loading templates...</p> : (
            <>
              <div className="flex items-center gap-4">
                <Select
                  label="Select Template"
                  selectedKeys={selectedTemplate ? [selectedTemplate.TempleteID.toString()] : []}
                  onSelectionChange={handleTemplateSelect}
                  className="w-1/2"
                  variant="flat"
                  size="sm"
                  color="danger"
                >
                  {templateList.map(temp => <SelectItem key={temp.TempleteID} value={temp.TempleteID.toString()}>{temp.TempleteName}</SelectItem>)}
                </Select>

                <Button size="sm" auto color="primary" onPress={handleAddNewTemplate}>Add New Template</Button>
                <Button size="sm" auto color="success" onPress={handleDownloadExcel} startContent={<FontAwesomeIcon icon={faDownload} />}>Excel Format</Button>
              </div>

              {isInputVisible && (
                <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
                  <Input clearable label="New Template Name" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} isRequired size="sm" variant="bordered" className="w-1/2" />
                  <Select label="Template Type" selectedKeys={[String(newTemplateType)]} size="sm" variant="bordered" className="w-1/2" onChange={(e) => setNewTemplateType(e.target.value)}>
                    {ENUM_TEMP_TYPE.map(list => <SelectItem key={list.key} value={list.value}>{list.value}</SelectItem>)}
                  </Select>
                  <Button type="submit" size="sm" color="danger">Submit</Button>
                </form>
              )}

              <div className="mt-6 flex justify-between items-center">
                <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} id="excel-upload" className="hidden" />
                <Button size="sm" auto color="success" onPress={handleAddRow}>+ Add New Row</Button>
              </div>

              <div style={{ height: 400, width: "100%", marginTop: 20 }}>
                {isExcelLoading ? (
                  <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>
                ) : (
                  <DataGrid
                    rows={excelTemplateData}
                    columns={columns}
                    getRowId={row => row.id}
                    hideFooter
                    columnHeaderHeight={35}
                    rowHeight={32}
                    processRowUpdate={handleProcessRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    disableSelectionOnClick
                    onProcessRowUpdateError={console.error}
                  />
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </MainContainer>
  );
};

export default ExcelMaster;
